// src/pages/EditFeedback.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface FeedbackData {
  id: number | null;
  customerComplaints: string;
  desiredFeatures: string;
  serviceIssues: string;
  escalationDetails: string;
}

interface Report {
  id: number;
  created_at: string;
}

const EditFeedback: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Dealer id from route
  const navigate = useNavigate();

  // State for feedback form data.
  const [formData, setFormData] = useState<FeedbackData>({
    id: null,
    customerComplaints: "",
    desiredFeatures: "",
    serviceIssues: "",
    escalationDetails: ""
  });

  // New state to store reports and the newest report id.
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Step 1: Fetch the dealer's reports.
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (Array.isArray(data) && data.length > 0) {
          // Assume reports are sorted descending by created_at.
          setReports(data);
          setNewestReportId(data[0].id);
        } else {
          // No report exists; automatically create one.
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          })
            .then((res) => res.json())
            .then((newReport) => setNewestReportId(newReport.id))
            .catch((err) => console.error("Error creating report", err));
        }
      })
      .catch((err) => console.error("Error fetching reports", err));
  }, [id]);

  // Step 2: With newestReportId available, fetch the feedback data via the report‑centric endpoint.
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/feedback`)
      .then((res) => res.json())
      .then((data) => {
        setFormData({
          id: data.id || null,
          customerComplaints: data.customer_complaints || "",
          desiredFeatures: data.desired_features || "",
          serviceIssues: data.service_issues || "",
          escalationDetails: data.escalation_details || ""
        });
      })
      .catch((err) => console.error("Error fetching feedback", err));
  }, [newestReportId, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 3: On form submission, update (or create) the feedback record using report‑centric endpoints.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      dealer_id: id,
      customerComplaints: formData.customerComplaints,
      desiredFeatures: formData.desiredFeatures,
      serviceIssues: formData.serviceIssues,
      escalationDetails: formData.escalationDetails
    };

    if (newestReportId) {
      if (formData.id) {
        // Update existing record.
        fetch(`/api/reports/${newestReportId}/feedback`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: formData.id, ...payload })
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error updating feedback", err));
      } else {
        // Create a new feedback record.
        fetch(`/api/reports/${newestReportId}/feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error creating feedback", err));
      }
    } else {
      console.error("No report found for this dealer.");
    }
  };

  // Styling objects remain unchanged.
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px"
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px"
  };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Feedback & Insights</h1>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Customer Complaints:</label>
          <textarea
            name="customerComplaints"
            style={inputStyle}
            value={formData.customerComplaints}
            onChange={handleChange}
            placeholder="Enter customer complaints"
          />
          <label style={labelStyle}>Desired Features:</label>
          <textarea
            name="desiredFeatures"
            style={inputStyle}
            value={formData.desiredFeatures}
            onChange={handleChange}
            placeholder="Enter desired features"
          />
          <label style={labelStyle}>Service Issues:</label>
          <textarea
            name="serviceIssues"
            style={inputStyle}
            value={formData.serviceIssues}
            onChange={handleChange}
            placeholder="Enter recurring service issues"
          />
          <label style={labelStyle}>Escalation Details:</label>
          <textarea
            name="escalationDetails"
            style={inputStyle}
            value={formData.escalationDetails}
            onChange={handleChange}
            placeholder="Enter escalation details if any"
          />
          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFeedback;
